import * as React from "react";
import { Eye, Download } from "lucide-react";
import { KnowledgeBaseDocument } from "../../../../types/navigationTypes";
import { Button } from "@/components/ui/button";
import { ICON_SIZES } from "@/lib/icon-sizes";
import { cn } from "@/lib/utils";
import FileIconSVG from "@/taskpane/shared/components/FileIconSVG";

interface DocumentItemProps {
  document: KnowledgeBaseDocument;
  onView: (doc: KnowledgeBaseDocument) => void;
  onDownload: (doc: KnowledgeBaseDocument) => void;
  isFirst?: boolean;
  /** Root-level files align with KB folder rows; nested files stay indented under a folder. */
  listIndent?: 'root' | 'nested';
}

const DocumentItem: React.FC<DocumentItemProps> = ({ 
  document, 
  onView, 
  onDownload,
  listIndent = 'nested',
}) => {
  const formatFileSize = (bytes?: number) => {
    if (!bytes) return '-';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getStatusDisplay = (status: 'pending' | 'processing' | 'completed' | 'failed') => {
    switch (status) {
      case 'completed':
        return { text: 'Ready', className: 'text-accent bg-muted/10' };
      case 'processing':
        return { text: 'Processing', className: 'text-brand-accent bg-brand-accent-soft' };
      case 'pending':
        return { text: 'Pending', className: 'text-muted-foreground bg-muted' };
      case 'failed':
        return { text: 'Failed', className: 'text-destructive bg-destructive/10' };
    }
  };

  const filename = document.name;
  const statusInfo = getStatusDisplay(document.status);

  return (
    <div
      className={cn(
        'grid grid-cols-[1fr_80px_100px_70px_80px] gap-2 py-2 px-4 items-center border-b border-gray-100 transition-colors cursor-default hover:bg-gray-50 bg-white',
        listIndent === 'root' ? 'pl-4' : 'pl-10'
      )}
    >
      {/* Name Column */}
      <div className="flex items-center gap-3 min-w-0 overflow-hidden">
        <FileIconSVG filename={filename} size={ICON_SIZES.sm} />
        <span className="text-xs text-gray-700 overflow-hidden text-ellipsis whitespace-nowrap">
          {filename}
        </span>
      </div>

      {/* Size Column */}
      <div className="text-xs text-gray-500">
        {formatFileSize(document.size)}
      </div>

      {/* Modified Column */}
      <div className="text-xs text-gray-500">
        {formatDate(document.updated_at || document.created_at)}
      </div>

      {/* Status Column */}
      <div>
        <span className={cn(
          "text-[10px] font-medium py-0.5 px-1.5 rounded uppercase tracking-wide",
          statusInfo.className
        )}>
          {statusInfo.text}
        </span>
      </div>

      {/* Actions Column */}
      <div className="flex gap-1 justify-end">
        <Button
          variant="ghost"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            onView(document);
          }}
          disabled={!document.url}
          title="View document"
          className="h-7 w-7 p-0 hover:bg-muted"
        >
          <Eye size={ICON_SIZES.sm} />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            onDownload(document);
          }}
          disabled={!document.url}
          title="Download"
          className="h-7 w-7 p-0 hover:bg-muted"
        >
          <Download size={ICON_SIZES.sm} />
        </Button>
      </div>
    </div>
  );
};

export default DocumentItem;






